<script setup lang="ts">
import type {Maybe} from '@/types'
import {Highlight, type Cell, type TableData} from '@/client/mentor/MessagesClient'
import {computed, ref} from 'vue'
import {isNullOrUndefined} from '@/utils/utils'
import {useScrollState} from '@/composables/useScrollState'

interface Props {
  tableData: TableData
}

const props = defineProps<Props>()

const tableContainerRef = ref<HTMLElement | null>(null)
const tableRef = ref<HTMLElement | null>(null)
const {isScrollable, isAtStart} = useScrollState(tableContainerRef, {direction: 'horizontal', contentRef: tableRef})

const columns = computed(() => props.tableData.cols)

const rows = computed(() =>
  props.tableData.rows.map((row) =>
    columns.value.map((column) => {
      const value = getCell(row, column)
      return {
        value: getCellDisplayValue(value?.v),
        classes: getCellClasses(value?.s),
      }
    })
  )
)

function getCell(row: Cell[], column: string): Cell | undefined {
  const index = props.tableData.cols.indexOf(column)
  return index >= 0 ? row[index] : undefined
}

function getCellDisplayValue(value: Maybe<unknown>): string {
  if (isNullOrUndefined(value)) return '-'
  return String(value)
}

function getCellClasses(status: Maybe<Highlight>): Record<string, boolean> {
  return {
    'text-olive': status === Highlight.POS,
    'text-red': status === Highlight.NEG,
    'opacity-50': status === Highlight.DIM,
    'font-bold': status === Highlight.BLD,
    italic: status === Highlight.ITL,
  }
}
</script>

<template>
  <div class="relative w-full print:w-fit print:max-w-full">
    <div
      ref="tableContainerRef"
      class="w-full overflow-x-auto rounded-new-xl border border-new-neutral-100 bg-new-neutral-0 print:w-fit print:max-w-full"
    >
      <table ref="tableRef" class="w-full table-auto text-base text-left">
        <thead class="bg-new-neutral-100 text-new-primary-default">
          <tr>
            <th v-for="column in columns" :key="column" class="px-3 py-2.5 align-top font-semi-bold whitespace-normal break-normal">
              {{ column }}
              <span v-if="tableData.unit?.[column]" class="ml-1 font-normal text-new-neutral-500">
                ({{ tableData.unit[column] }})
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in rows"
            :key="rowIndex"
            class="border-t border-new-neutral-100 text-verydarkgrey even:bg-new-neutral-50 odd:bg-white"
          >
            <td
              v-for="(cell, columnIndex) in row"
              :key="`${rowIndex}-${columnIndex}`"
              class="px-3 py-2.5 whitespace-nowrap"
              :class="cell.classes"
            >
              {{ cell.value }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="isScrollable && isAtStart"
      class="pointer-events-none absolute inset-y-0 right-0 w-10 rounded-r-new-xl bg-gradient-to-l from-new-neutral-0 to-transparent print:hidden"
    ></div>
  </div>
</template>
